import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import MobileNav from '@/components/MobileNav';
import { ChevronLeft, ChevronRight, BookOpen, Clock, Zap, CheckCircle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ModuleView() {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const { token } = useAuth();
  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [moduleId]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [moduleRes, progressRes] = await Promise.all([
        axios.get(`${API}/education/modules/${moduleId}`, { headers }),
        axios.get(`${API}/education/progress`, { headers }),
      ]);
      setModule(moduleRes.data);
      setProgress(progressRes.data);
    } catch (err) {
      console.error('Failed to fetch module:', err);
    } finally {
      setLoading(false);
    }
  };

  const isLessonCompleted = (lessonId) => {
    return progress?.completed_lessons?.includes(lessonId) || false;
  };

  const getModuleProgress = () => {
    if (!module?.lessons || module.lessons.length === 0) return 0;
    const completed = module.lessons.filter(l => isLessonCompleted(l.id)).length;
    return Math.round((completed / module.lessons.length) * 100);
  };

  const getFirstUnfinishedLesson = () => {
    return module?.lessons?.find(l => !isLessonCompleted(l.id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#ff6b35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-4">
        <p className="text-gray-400">Module introuvable</p>
        <Button onClick={() => navigate('/academie')} variant="outline">Retour à l'Académie</Button>
      </div>
    );
  }

  const modProgress = getModuleProgress();
  const firstUnfinished = getFirstUnfinishedLesson();

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="academie" />

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <button onClick={() => navigate('/academie')} className="hover:text-[#ff6b35] transition-colors">Académie</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{module.title}</span>
        </div>

        {/* Module Header */}
        <div className="glass rounded-2xl p-8 mb-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-4xl">{module.icon?.replace(/[🟢🟡🟠🔴]/g, '') || '📚'}</span>
                <div>
                  <h1 className="text-2xl font-bold text-white">{module.title}</h1>
                  <p className="text-gray-400 text-sm mt-1">{module.description}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                <span className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  {module.lessons_count} leçons
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {module.duration}
                </span>
              </div>

              {modProgress > 0 && (
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">Progression</span>
                    <span className="text-[#ff6b35] font-semibold">{modProgress}%</span>
                  </div>
                  <Progress value={modProgress} className="h-3" />
                </div>
              )}
            </div>

            {firstUnfinished && (
              <Button
                data-testid="continue-module-btn"
                onClick={() => navigate(`/academie/module/${moduleId}/lesson/${firstUnfinished.id}`)}
                className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold"
              >
                {modProgress > 0 ? 'Continuer' : 'Commencer'} →
              </Button>
            )}
            {modProgress === 100 && (
              <div className="flex items-center gap-2 text-green-400 bg-green-400/10 px-4 py-2 rounded-full">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-semibold">Module Complété !</span>
              </div>
            )}
          </div>
        </div>

        {/* Lessons List */}
        <div className="space-y-3">
          <h2 className="text-lg font-bold mb-4">Leçons du module</h2>
          {module.lessons && module.lessons.length > 0 ? (
            module.lessons.map((lesson, idx) => {
              const completed = isLessonCompleted(lesson.id);
              const isNext = !completed && idx === 0 || (!completed && module.lessons[idx - 1] && isLessonCompleted(module.lessons[idx - 1].id));

              return (
                <div
                  key={lesson.id}
                  data-testid={`lesson-item-${lesson.id}`}
                  onClick={() => navigate(`/academie/module/${moduleId}/lesson/${lesson.id}`)}
                  className={`glass rounded-xl p-5 cursor-pointer transition-all border ${
                    completed
                      ? 'border-green-400/20 hover:border-green-400/40'
                      : isNext
                      ? 'border-[#ff6b35]/30 hover:border-[#ff6b35]/50'
                      : 'border-transparent hover:border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      completed ? 'bg-green-400/10' : 'bg-[#ff6b35]/10'
                    }`}>
                      {completed ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <span className="text-[#ff6b35] font-bold">{idx + 1}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold ${completed ? 'text-green-400' : 'text-white'}`}>{lesson.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {lesson.duration_minutes} min
                        </span>
                        <span className="flex items-center gap-1">
                          <Zap className="w-3 h-3 text-[#ff6b35]" />
                          +{lesson.xp_reward} XP
                        </span>
                        {lesson.quiz && lesson.quiz.length > 0 && (
                          <span className="text-purple-400">{lesson.quiz.length} questions</span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="glass rounded-xl p-8 text-center">
              <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">Les leçons de ce module arrivent bientôt !</p>
              <p className="text-gray-500 text-sm mt-1">Nous préparons du contenu de qualité pour vous.</p>
            </div>
          )}
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <Button
            onClick={() => navigate('/academie')}
            variant="outline"
            className="border-gray-700 text-gray-400"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Retour à l'Académie
          </Button>
        </div>
      </div>
    </div>
  );
}
